import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Default STUN servers (TURN will be fetched from backend)
const DEFAULT_ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

// Check if iOS
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

const log = (msg, data = null) => {
  const ts = new Date().toISOString().substr(11, 12);
  console.log(`[Call ${ts}] ${msg}`, data || '');
};

// Generate a ringtone using Web Audio API
const playRingtone = (audioCtx) => {
  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();
  osc.connect(gain);
  gain.connect(audioCtx.destination);
  osc.frequency.value = 440;
  osc.type = 'sine';
  gain.gain.value = 0.15;

  // Ring pattern: 1s on, 2s off
  const now = audioCtx.currentTime;
  for (let i = 0; i < 10; i++) {
    gain.gain.setValueAtTime(0.15, now + i * 3);
    gain.gain.setValueAtTime(0, now + i * 3 + 1);
  }
  osc.start(now);
  osc.stop(now + 30);
  return { osc, gain };
};

const CallOverlay = ({ callId, callerName, calleeName, isCaller, onEnd }) => {
  const [status, setStatus] = useState(isCaller ? 'ringing' : 'incoming');
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState('');

  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const iceQueueRef = useRef(new Set());
  const mountedRef = useRef(true);
  const timeoutRef = useRef(null);
  const ringCtxRef = useRef(null);
  const ringOscRef = useRef(null);
  const answerReceivedRef = useRef(false); // Track if answer was received to prevent duplicate offers
  const callerStartedRef = useRef(false); // Prevent multiple startAsCaller calls

  const stopRingtone = useCallback(() => {
    try {
      if (ringOscRef.current) { ringOscRef.current.osc.stop(); ringOscRef.current = null; }
      if (ringCtxRef.current) { ringCtxRef.current.close(); ringCtxRef.current = null; }
    } catch (e) {}
  }, []);

  const startRingtone = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ringCtxRef.current = ctx;
      ringOscRef.current = playRingtone(ctx);
    } catch (e) {}
  }, []);

  const cleanup = useCallback(() => {
    mountedRef.current = false;
    stopRingtone();
    if (pollRef.current) clearInterval(pollRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (pcRef.current) { try { pcRef.current.close(); } catch (e) {} pcRef.current = null; }
    if (localStreamRef.current) { localStreamRef.current.getTracks().forEach(t => t.stop()); localStreamRef.current = null; }
  }, [stopRingtone]);

  const endCall = useCallback(async () => {
    try { await axios.post(`${API}/call/${callId}/signal`, { status: 'ended' }); } catch (e) {}
    cleanup();
    onEnd();
  }, [callId, cleanup, onEnd]);

  const setupPeerConnection = useCallback(async () => {
    log('Setting up peer connection...', { isIOS });
    
    // Fetch TURN credentials from backend
    let iceServers = DEFAULT_ICE_SERVERS;
    try {
      const { data } = await axios.get(`${API}/turn/credentials`);
      if (Array.isArray(data) && data.length > 0) {
        // For iOS, prioritize TURN over TCP/TLS (more reliable)
        if (isIOS) {
          iceServers = data.filter(s => 
            s.urls?.includes('turns:') || 
            s.urls?.includes('transport=tcp') ||
            s.urls?.includes('stun:')
          );
          // If no TCP/TLS servers, use all
          if (iceServers.length < 2) iceServers = data;
        } else {
          iceServers = data;
        }
        log('Got ICE servers from backend', iceServers.length);
      }
    } catch (e) {
      log('Failed to fetch TURN credentials, using defaults', e.message);
    }
    
    let stream;
    try {
      // iOS specific: request audio with echo cancellation
      stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      log('Got local audio stream');
    } catch (e) {
      log('Failed to get audio stream', e.message);
      onEnd();
      return null;
    }
    localStreamRef.current = stream;
    
    // Create peer connection with fetched ICE servers
    const pc = new RTCPeerConnection({ 
      iceServers: iceServers,
      iceCandidatePoolSize: 10,
      iceTransportPolicy: 'all',
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require'
    });
    pcRef.current = pc;

    stream.getTracks().forEach(t => {
      pc.addTrack(t, stream);
      log('Added local track', t.kind);
    });

    pc.ontrack = (e) => {
      log('Received remote track', e.streams.length, e.track.kind);
      if (e.streams[0]) {
        log('Setting remote stream to audio element');
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = e.streams[0];
          // iOS requires user interaction - try to play
          const playPromise = remoteAudioRef.current.play();
          if (playPromise !== undefined) {
            playPromise.then(() => {
              log('Remote audio playing successfully');
            }).catch((err) => {
              log('Remote audio play error (will retry on user interaction)', err.message);
            });
          }
        }
      }
    };

    pc.onicecandidate = async (e) => {
      if (e.candidate) {
        log('ICE candidate', { type: e.candidate.type, protocol: e.candidate.protocol, address: e.candidate.address?.substring(0, 15) });
        try {
          await axios.post(`${API}/call/${callId}/signal`, {
            ice: JSON.stringify(e.candidate),
            role: isCaller ? 'caller' : 'callee'
          });
        } catch (err) {
          log('Failed to send ICE candidate', err.message);
        }
      } else {
        log('ICE gathering complete');
      }
    };

    pc.oniceconnectionstatechange = () => {
      if (!mountedRef.current) return;
      const state = pc.iceConnectionState;
      log('ICE connection state', state);
      log('ICE gathering state', pc.iceGatheringState);
      
      if (state === 'connected' || state === 'completed') {
        setStatus('active');
        setConnectionInfo('');
        stopRingtone();
        if (!timerRef.current) timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
      } else if (state === 'checking') {
        setConnectionInfo('Conectando...');
      } else if (state === 'failed') {
        setConnectionInfo('Conexao falhou');
        log('ICE connection failed');
        // Log detailed stats
        pc.getStats().then(stats => {
          stats.forEach(report => {
            if (report.type === 'candidate-pair') {
              log('Candidate pair', { state: report.state, local: report.localCandidateId, remote: report.remoteCandidateId, nominated: report.nominated });
            }
            if (report.type === 'local-candidate' || report.type === 'remote-candidate') {
              log('Candidate', { type: report.candidateType, protocol: report.protocol, address: report.address });
            }
          });
        });
        setTimeout(() => { if (mountedRef.current) endCall(); }, 5000);
      } else if (state === 'disconnected') {
        setConnectionInfo('Reconectando...');
      }
    };

    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      log('Connection state', state);
      if (['disconnected', 'failed', 'closed'].includes(state)) {
        if (mountedRef.current) {
          setConnectionInfo('Chamada encerrada');
          setTimeout(() => endCall(), 1500);
        }
      }
    };

    pc.onsignalingstatechange = () => {
      log('Signaling state', pc.signalingState);
    };

    return pc;
  }, [callId, isCaller, endCall, onEnd, stopRingtone]);

  const processIce = useCallback(async (pc, candidates, role) => {
    for (const ice of (candidates || [])) {
      if (iceQueueRef.current.has(ice)) continue;
      iceQueueRef.current.add(ice);
      try {
        const cand = JSON.parse(ice);
        log(`Adding ICE candidate from ${role}`, cand.type);
        await pc.addIceCandidate(new RTCIceCandidate(cand));
      } catch (e) {
        log('Failed to add ICE candidate', e.message);
      }
    }
  }, []);

  // Caller flow
  const startAsCaller = useCallback(async () => {
    // Prevent multiple calls
    if (callerStartedRef.current) {
      log('startAsCaller called but already started, ignoring');
      return;
    }
    callerStartedRef.current = true;
    
    log('Starting as caller...');
    const pc = await setupPeerConnection();
    if (!pc) return;

    // Small delay for iOS Safari to initialize properly
    if (isIOS) {
      await new Promise(r => setTimeout(r, 500));
    }

    try {
      log('Creating offer...');
      const offer = await pc.createOffer({ 
        offerToReceiveAudio: true,
        voiceActivityDetection: true
      });
      log('Setting local description...');
      await pc.setLocalDescription(offer);
      
      // Wait for ICE gathering to have at least some candidates
      if (pc.iceGatheringState !== 'complete') {
        await new Promise(resolve => {
          const checkGathering = () => {
            if (pc.iceGatheringState === 'complete' || pc.iceGatheringState === 'gathering') {
              resolve();
            } else {
              setTimeout(checkGathering, 100);
            }
          };
          setTimeout(checkGathering, 100);
          // Max wait 2 seconds
          setTimeout(resolve, 2000);
        });
      }
      
      log('Sending offer to server...');
      await axios.post(`${API}/call/${callId}/signal`, { offer: JSON.stringify(offer) });
      log('Offer sent successfully');
    } catch (e) {
      log('Error creating/sending offer', e.message);
      setConnectionInfo('Erro ao criar chamada');
      setTimeout(() => { if (mountedRef.current) endCall(); }, 2000);
      return;
    }

    startRingtone();

    // Timeout after 60s
    timeoutRef.current = setTimeout(() => {
      if (mountedRef.current && status === 'ringing') {
        log('Call timeout - no answer');
        setConnectionInfo('Sem resposta');
        stopRingtone();
        setTimeout(() => endCall(), 2000);
      }
    }, 60000);

    pollRef.current = setInterval(async () => {
      if (!mountedRef.current) return;
      try {
        const { data } = await axios.get(`${API}/call/${callId}`);
        
        if (data.status === 'ended' || data.status === 'rejected') {
          log('Call ended/rejected by callee');
          if (mountedRef.current) { cleanup(); onEnd(); }
          return;
        }
        
        // Only process answer once
        if (data.answer && !answerReceivedRef.current && pc.signalingState !== 'stable') {
          answerReceivedRef.current = true;
          stopRingtone();
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          log('Received answer, setting remote description...');
          setConnectionInfo('Conectando...');
          try {
            await pc.setRemoteDescription(new RTCSessionDescription(JSON.parse(data.answer)));
            log('Remote description set successfully');
            // Process ICE candidates AFTER setting remote description
            await processIce(pc, data.ice_callee, 'callee');
          } catch (e) {
            log('Error setting remote description', e.message);
            setConnectionInfo('Erro de conexao');
          }
        } else if (answerReceivedRef.current) {
          // Only process new ICE candidates after answer is set
          await processIce(pc, data.ice_callee, 'callee');
        }
      } catch (e) {
        log('Poll error', e.message);
      }
    }, 500);  // Poll faster for ICE candidates
  }, [callId, setupPeerConnection, cleanup, onEnd, startRingtone, stopRingtone, processIce, endCall, status]);

  // Callee flow
  const answerCall = useCallback(async () => {
    log('Answering call...');
    stopRingtone();
    setStatus('connecting');
    setConnectionInfo('Conectando...');
    const pc = await setupPeerConnection();
    if (!pc) return;

    try {
      log('Fetching call data...');
      const { data } = await axios.get(`${API}/call/${callId}`);
      if (data.offer) {
        log('Setting remote description (offer)...');
        await pc.setRemoteDescription(new RTCSessionDescription(JSON.parse(data.offer)));
        log('Creating answer...');
        const answer = await pc.createAnswer();
        log('Setting local description (answer)...');
        await pc.setLocalDescription(answer);
        log('Sending answer to server...');
        await axios.post(`${API}/call/${callId}/signal`, { answer: JSON.stringify(answer) });
        log('Answer sent successfully');
        await processIce(pc, data.ice_caller, 'caller');
      } else {
        log('No offer found in call data');
        setConnectionInfo('Chamada indisponivel');
        setTimeout(() => { if (mountedRef.current) endCall(); }, 2000);
        return;
      }
    } catch (e) {
      log('Error answering call', e.message);
      setConnectionInfo('Erro ao conectar');
      setTimeout(() => { if (mountedRef.current) endCall(); }, 2000);
      return;
    }

    pollRef.current = setInterval(async () => {
      if (!mountedRef.current) return;
      try {
        const { data: callData } = await axios.get(`${API}/call/${callId}`);
        if (callData.status === 'ended') { 
          log('Call ended by caller');
          cleanup(); 
          onEnd(); 
          return; 
        }
        await processIce(pc, callData.ice_caller, 'caller');
      } catch (e) {
        log('Poll error', e.message);
      }
    }, 500);  // Poll faster for ICE candidates
  }, [callId, setupPeerConnection, cleanup, onEnd, stopRingtone, processIce, endCall]);

  const rejectCall = useCallback(async () => {
    stopRingtone();
    try { await axios.post(`${API}/call/${callId}/signal`, { status: 'rejected' }); } catch (e) {}
    cleanup();
    onEnd();
  }, [callId, cleanup, onEnd, stopRingtone]);

  useEffect(() => {
    if (isCaller) {
      startAsCaller();
    } else {
      // Callee: start ringtone
      startRingtone();
    }
    return () => { mountedRef.current = false; };
  }, [isCaller, startAsCaller, startRingtone]);

  const toggleMute = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(t => { t.enabled = muted; });
      setMuted(!muted);
    }
  };

  const formatDuration = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  const pulseBg = status === 'ringing' || status === 'incoming';
  const displayName = isCaller ? calleeName : callerName;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
      style={{ background: 'linear-gradient(180deg, #0a0a0c 0%, #111118 50%, #0a0a0c 100%)' }}
      data-testid="call-overlay"
    >
      {/* Audio element - iOS requires these attributes */}
      <audio 
        ref={remoteAudioRef} 
        autoPlay 
        playsInline 
        controls={false}
        muted={false}
        style={{ display: 'none' }}
      />

      {/* Pulse animation */}
      {pulseBg && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {[0, 1, 2].map(i => (
            <motion.div key={i}
              className="absolute w-40 h-40 rounded-full border border-[#c0c0c0]/10"
              animate={{ scale: [1, 2.5], opacity: [0.3, 0] }}
              transition={{ duration: 2.5, repeat: Infinity, delay: i * 0.8 }}
            />
          ))}
        </div>
      )}

      {/* Avatar */}
      <motion.div
        animate={pulseBg ? { scale: [1, 1.05, 1] } : {}}
        transition={{ duration: 2, repeat: Infinity }}
        className="w-28 h-28 rounded-full bg-gradient-to-br from-[#c0c0c0]/20 to-white/5 border border-[#c0c0c0]/15 flex items-center justify-center mb-6"
      >
        <span className="text-4xl font-['Jost'] text-[#c0c0c0]/60 font-light">
          {displayName?.charAt(0)?.toUpperCase()}
        </span>
      </motion.div>

      <h2 className="text-xl font-['Jost'] font-light text-white/80 tracking-wide mb-1" data-testid="call-name">
        {displayName}
      </h2>

      <p className="text-sm font-['Jost'] text-white/30 mb-10" data-testid="call-status">
        {status === 'ringing' && 'Chamando...'}
        {status === 'incoming' && 'Chamada recebida'}
        {status === 'connecting' && (connectionInfo || 'Conectando...')}
        {status === 'active' && formatDuration(duration)}
        {connectionInfo && status !== 'connecting' && status !== 'active' && (
          <span className="block text-[11px] text-white/20 mt-1">{connectionInfo}</span>
        )}
      </p>

      {/* Buttons */}
      <div className="flex items-center gap-6">
        {status === 'incoming' ? (
          /* Callee: Botões Rejeitar (vermelho) e Aceitar (verde) */
          <>
            <motion.button whileTap={{ scale: 0.9 }} onClick={rejectCall}
              className="w-16 h-16 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center"
              data-testid="call-reject-btn">
              <svg className="w-7 h-7 text-red-400 rotate-[135deg]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.15 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
              </svg>
            </motion.button>
            <motion.button whileTap={{ scale: 0.9 }} onClick={answerCall}
              className="w-16 h-16 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center"
              data-testid="call-accept-btn">
              <svg className="w-7 h-7 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.15 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
              </svg>
            </motion.button>
          </>
        ) : status === 'ringing' ? (
          /* Caller chamando: Apenas botão vermelho para cancelar */
          <motion.button whileTap={{ scale: 0.9 }} onClick={endCall}
            className="w-16 h-16 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center"
            data-testid="call-cancel-btn">
            <svg className="w-7 h-7 text-red-400 rotate-[135deg]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6.62 10.79c1.44 2.83 3.76 5.15 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
            </svg>
          </motion.button>
        ) : (
          /* Chamada ativa ou conectando: Mute + Desligar */
          <>
            <motion.button whileTap={{ scale: 0.9 }} onClick={toggleMute}
              className={`w-14 h-14 rounded-full flex items-center justify-center transition border ${
                muted ? 'bg-white/15 border-white/20' : 'bg-white/5 border-white/10'
              }`} data-testid="call-mute-btn">
              <svg className={`w-5 h-5 ${muted ? 'text-white/60' : 'text-white/30'}`} fill="currentColor" viewBox="0 0 20 20">
                {muted ? (
                  <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                ) : (
                  <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                )}
              </svg>
            </motion.button>

            <motion.button whileTap={{ scale: 0.9 }} onClick={endCall}
              className="w-16 h-16 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center"
              data-testid="call-end-btn">
              <svg className="w-7 h-7 text-red-400 rotate-[135deg]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.15 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
              </svg>
            </motion.button>
          </>
        )}
      </div>

      <p className="absolute bottom-8 text-[10px] font-['Jost'] text-white/10 tracking-[0.3em] uppercase">
        WClub Voice
      </p>
    </motion.div>
  );
};

export default CallOverlay;
